import { css, html, nothing } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { customElement, property } from 'lit/decorators.js';
import type { ActivityCode, ActivityCodeCommon } from 'src/store/selectors';
import * as dabihStore from 'src/store';
import '../../library/components/d-activity-codes-container.js';
import '../../library/editors/elements/d-edit-text.js';
import '../../library/elements/d-section.js';
import '../../library/elements/d-smooth-resize.js';
import { DPrepareBehavior } from './d-prepare-behavior.js';
import './d-prepare-employee-row.js';
import './d-robot-header.js';
import { AccountStateEmployeeViewModelAccessLevelEnum } from '@trinnvis/dabih-api-client';
import type { PrepareEmployeeRowItem } from './prepare-employee-row-item';
import { t } from 'src/i18n';

/**
 *
 */
@customElement('d-prepare-step-0')
export class DPrepareStep0 extends DPrepareBehavior {
  static readonly styles = [
    ...super.styles,
    css`
      :host {
        display: block;
        background: var(--outskirts-background-color);
        min-height: 100vh;
      }

      d-activity-codes-container {
        margin-left: 4px;
        margin-bottom: 16px;
      }

      d-prepare-employee-row:first-child::content .delete-employee {
        display: none;
      }

      a {
        color: white;
        text-decoration: none;
      }

      a:hover {
        color: black;
      }

      .person-row {
        display: flex;
      }

      d-prepare-employee-row {
        flex: 1;
      }

      .delete-employee-wrapper {
        flex: none;
        display: flex;
        align-items: center;
        justify-content: flex-end;
        position: relative;
        width: 30px;
      }

      .delete-employee {
        width: 30px;
        height: 30px;
        background: url('/images/x-thin-black.svg') 100% 50% no-repeat;
        background-size: 24px 24px;
        opacity: 0.4;
        cursor: pointer;
      }

      .delete-employee:hover {
        opacity: 0.8;
      }

      d-action {
        margin-left: -8px;
      }
    `,
  ];
  @property({ type: Boolean })
  singleUser = false;
  @property({ type: String })
  ownerEmail = '';
  @property({ type: Array })
  employees: PrepareEmployeeRowItem[] = [];
  @property({ type: Array })
  codes: ActivityCode[] = [];
  @property({ type: Array })
  commonActivityCodes: ActivityCodeCommon[] = [];
  @property({ type: Array })
  activityCodes: string[] = [];
  @property({ type: String })
  sector = '';
  @property({ type: Array })
  professions: string[] = [];
  @property({ type: Array })
  associationTypes: string[] = [];
  @property({ type: Boolean })
  openDetails = true;
  @property({ type: String })
  deletingEmployeeUuid = '';
  protected next = 'step1';
  private get robotMessage() {
    return t('account.prepareStep0.robotMessage');
  }

  get singleUserOption() {
    return this.employees.length <= 1;
  }

  deletingEmployeeRow(deletingEmployeeUuid: string, itemUuid: string) {
    return deletingEmployeeUuid === itemUuid;
  }

  _forwardOrFinish(e) {
    if (this.isValid()) {
      if (this.singleUser) {
        this._finish(e);
      } else {
        this._forward(e);
      }
    }
  }

  render() {
    const hasActivityCodes = this.codes && this.codes.length > 0;
    const activityCodesHeader =
      this.sector === 'HEALTH'
        ? t('account.prepareStep0.activityCodesHeaderHealth')
        : t('account.prepareStep0.activityCodesHeaderOther');
    const activityCodesPrompt =
      this.sector === 'HEALTH'
        ? t('account.prepareStep0.activityCodesPromptHealth')
        : t('account.prepareStep0.activityCodesPromptOther');

    return html`
      <d-robot-header .message=${this.robotMessage}></d-robot-header>
      <div class="outskirtsContent">
        <form id="accountConfig1Form" @submit=${(e) => this._forwardOrFinish(e)}>
          ${!hasActivityCodes
            ? nothing
            : html` <div>
                <h2>${activityCodesHeader}</h2>
                <p class="prompt">${activityCodesPrompt}</p>
                <d-activity-codes-container
                  outskirts
                  .activityCodes=${this.activityCodes}
                  .codes=${this.codes}
                  .commonActivityCodes=${this.commonActivityCodes}
                ></d-activity-codes-container>
              </div>`}
          <d-smooth-resize>
            ${this.singleUserOption
              ? html`
                  <div>
                    <h2>${t('account.prepareStep0.simplifiedVersionHeading')}</h2>
                    <p class="prompt">${unsafeHTML(t('account.prepareStep0.simplifiedVersionPrompt'))}</p>
                    <d-checkbox
                      outskirts
                      ?checked=${this.singleUser}
                      @checked-changed=${this.onSingleUserChanged}
                      .optionLabel=${t('account.prepareStep0.simplifiedVersionOption')}
                    >
                    </d-checkbox>
                  </div>
                `
              : nothing}
          </d-smooth-resize>
          <h2>${t('account.prepareStep0.staffHeading')}</h2>
          <p class="prompt">${t('account.prepareStep0.staffPrompt')}</p>

          <d-smooth-resize>
            ${this.employees.map((item, index) => {
              return html` ${!this.deletingEmployeeRow(this.deletingEmployeeUuid, item.uuid)
                ? html`
                    <div class="person-row">
                      <d-prepare-employee-row
                        .index=${index}
                        .item=${item}
                        .professions=${this.professions}
                        .associationTypes=${this.associationTypes}
                        @employee-changed=${this.onEmployeeChanged}
                      >
                      </d-prepare-employee-row>
                      <div class="delete-employee-wrapper">
                        ${index !== 0
                          ? html` <div class="delete-employee" @click=${() => this.onDeleteEmployee(item.uuid)}></div> `
                          : nothing}
                      </div>
                    </div>
                  `
                : nothing}`;
            })}
          </d-smooth-resize>
          ${this.singleUser
            ? nothing
            : html` <d-action outskirts @click=${() => this.onAddEmployee()}
                >${t('account.prepareStep0.addMore')}</d-action
              >`}
          <div class="buttons">
            <button class="right" @click=${(e) => this._cancel(e)} type="button">
              ${t('account.prepareStep0.cancel')}
            </button>
            <button class="right nextPage" type="submit">${t('account.prepareStep0.next')}</button>
          </div>
        </form>
      </div>
    `;
  }

  isValid() {
    const emails: string[] = [];
    let valid = true;
    this.employees.forEach(function (e) {
      const normalizedEmail = e.email.toLowerCase().trim();
      if (normalizedEmail) {
        if (!emails.includes(normalizedEmail)) {
          emails.push(normalizedEmail);
        } else {
          valid = false;
        }
      }
    });
    if (!valid) {
      alert(t('account.prepareStep0.duplicateEmailAlert'));
    }
    return valid;
  }

  private onAddEmployee() {
    const data = {
      uuid: dabihStore.createUuid(),
      firstName: '',
      lastName: '',
      associationType: '',
      profession: '',
      email: '',
      accessLevel: AccountStateEmployeeViewModelAccessLevelEnum.User,
    };
    this.employees = [...this.employees, data];
    this.dispatchEvent(
      new CustomEvent<{ employees: PrepareEmployeeRowItem[] }>('employees-changed', {
        bubbles: true,
        composed: true,
        detail: { employees: this.employees },
      }),
    );
  }

  private onDeleteEmployee(uuid: string) {
    this.deletingEmployeeUuid = uuid;
    setTimeout(() => {
      this.employees = this.employees.filter((employee) => employee.uuid !== uuid);
      this.dispatchEvent(
        new CustomEvent<{ employees: PrepareEmployeeRowItem[] }>('employees-changed', {
          bubbles: true,
          composed: true,
          detail: { employees: this.employees },
        }),
      );
    }, 500);
  }

  private onEmployeeChanged(e: CustomEvent<{ item: PrepareEmployeeRowItem }>) {
    e.stopPropagation();
    this.employees = this.employees.map((employee) =>
      employee.uuid === e.detail.item.uuid ? e.detail.item : employee,
    );
    this.dispatchEvent(
      new CustomEvent<{ employees: PrepareEmployeeRowItem[] }>('employees-changed', {
        bubbles: true,
        composed: true,
        detail: { employees: this.employees },
      }),
    );
  }

  private onSingleUserChanged(e: CustomEvent<{ checked: boolean }>) {
    e.stopPropagation();
    this.singleUser = e.detail.checked;
    this.dispatchEvent(
      new CustomEvent('single-user-changed', {
        bubbles: true,
        composed: true,
        detail: { checked: this.singleUser },
      }),
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-prepare-step-0': DPrepareStep0;
  }
}
